import type { ShippingAddress } from './components/OrderSummary.js';

export interface SampleCardDescriptor {
  revision: number;
  last4: string;
  complete: boolean;
  billingSame: boolean;
}

export interface PaymentAgreement {
  key: string;
  payment: boolean;
  recurring: boolean;
}

export interface AppliedPayment {
  method: 'saved' | 'new';
  sample: SampleCardDescriptor | null;
  agreement: PaymentAgreement;
}

export interface OrderDraft {
  quantities: Record<string, number>;
  subscription: boolean;
  frequencyDays: string;
  address: ShippingAddress;
  addressConfirmed: boolean;
  revision: number;
}

export interface PreviewQuoteRequest {
  customerId: string;
  workId: string;
  contextKey: string;
  // W36: `handle` is the pricing-engine product key; the example sku and
  // cents exist only for the design preview's fixture quote comparator.
  basket: Array<{ sku: string; handle?: string; quantity: number; exampleUnitCents: number }>;
  address: ShippingAddress;
  subscription: boolean;
  frequencyDays: string;
}

// An injected fixture adapter permits interaction testing. It is deliberately
// not a provider quote, payment token, command, or production checkout contract.
export interface PreviewQuote {
  kind: 'design-preview';
  contextKey: string;
  revision: string;
  taxCents: number;
  totalCents: number;
}

export type PreviewQuoteLoader = (request: PreviewQuoteRequest) => Promise<PreviewQuote>;

export const DEFAULT_REVIEW_ADDRESS: ShippingAddress = {
  recipient: 'Sarah Mitchell',
  street: '8124 Meadow Lane',
  line2: 'Barn entrance',
  city: 'Nashville',
  region: 'TN',
  postal: '37221',
  country: 'United States',
};

export function emptyAgreement(key = ''): PaymentAgreement {
  return { key, payment: false, recurring: false };
}

export function initialPayment(): AppliedPayment {
  return { method: 'saved', sample: null, agreement: emptyAgreement() };
}

export function paymentAgreementKey(
  orderContext: string,
  method: AppliedPayment['method'],
  sample: SampleCardDescriptor | null,
): string {
  return JSON.stringify([orderContext, method, method === 'new' ? sample : null]);
}

export function validPreviewQuote(
  quote: PreviewQuote,
  request: PreviewQuoteRequest,
): boolean {
  const subtotal = request.basket.reduce((sum, line) => sum + line.quantity * line.exampleUnitCents, 0);
  return quote.kind === 'design-preview'
    && quote.contextKey === request.contextKey
    && typeof quote.revision === 'string'
    && quote.revision.length > 0
    && Number.isSafeInteger(quote.taxCents)
    && quote.taxCents >= 0
    && Number.isSafeInteger(quote.totalCents)
    && quote.totalCents === subtotal + quote.taxCents;
}

export function paymentLabel(payment: AppliedPayment): string {
  return payment.method === 'new' && payment.sample?.complete
    ? 'Sample card ending in ' + payment.sample.last4
    : 'Visa ending in 4242';
}
